// The bot's `/api/events` stream, read with `fetch` rather than `EventSource`: `EventSource`
// cannot send headers, and the key belongs in `x-secret-key`, not in a URL that proxies and
// access logs record. Like the fetcher, this is a sanctioned direct `fetch`.
// [[spec-reactive-arb-bot-ui-over-server-sent-events-generated-from-one-rust-event-enum]]

import { AppEvent } from "@/generated/api/model";
import { resolveApiUrl, SECRET_KEY_HEADER } from "@/lib/api/fetcher";
import { currentKey, signOut } from "@/lib/auth/session";

export const EVENTS_PATH = "/api/events";

export type StreamStatus = "connecting" | "live" | "reconnecting";

const KNOWN = new Set<string>(Object.values(AppEvent));

/**
 * Feeds raw stream text in, calls `onData` with each complete event's data. Only `data:` lines
 * matter here; keep-alive comments (`:`) and other fields are skipped.
 */
export function createSseParser(onData: (data: string) => void) {
    let buffer = "";
    let data: string[] = [];
    return (chunk: string) => {
        buffer += chunk;
        const lines = buffer.split(/\r\n|\r|\n/);
        buffer = lines.pop()!;
        for (const line of lines) {
            if (line === "") {
                if (data.length > 0) onData(data.join("\n"));
                data = [];
            } else if (line.startsWith("data:")) {
                data.push(line.slice(line.startsWith("data: ") ? 6 : 5));
            }
        }
    };
}

/** An event this build does not know (a newer bot) is dropped: the reconnect after a deploy refetches everything. */
export function parseEvent(data: string): AppEvent | null {
    try {
        const value: unknown = JSON.parse(data);
        return typeof value === "string" && KNOWN.has(value) ? (value as AppEvent) : null;
    } catch {
        return null;
    }
}

interface StreamOptions {
    onEvent: (event: AppEvent) => void;
    onStatus: (status: StreamStatus) => void;
    /** A reconnect after the stream was live: events are not replayed, so whatever was missed is unknown. */
    onReconnected: () => void;
    baseDelayMs?: number;
    maxDelayMs?: number;
}

/** Opens the stream and keeps it open, with exponential backoff, until the returned stop is called. */
export function openEventStream({
    onEvent,
    onStatus,
    onReconnected,
    baseDelayMs = 500,
    maxDelayMs = 15_000,
}: StreamOptions): () => void {
    let stopped = false;
    let wasLive = false;
    let failures = 0;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const abort = new AbortController();

    const connect = async () => {
        const key = currentKey();
        const headers = new Headers({ Accept: "text/event-stream" });
        if (key !== null) headers.set(SECRET_KEY_HEADER, key);
        try {
            const response = await fetch(resolveApiUrl(EVENTS_PATH), { headers, signal: abort.signal });
            if (response.status === 401 && key !== null) {
                // Signing out unmounts whatever opened this stream, which stops it.
                signOut("rejected");
                return;
            }
            if (!response.ok || response.body === null) throw new Error(`events answered ${response.status}`);

            failures = 0;
            onStatus("live");
            if (wasLive) onReconnected();
            wasLive = true;

            const parse = createSseParser((data) => {
                const event = parseEvent(data);
                if (event !== null) onEvent(event);
            });
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                parse(decoder.decode(value, { stream: true }));
            }
        } catch {
            // A dropped connection or a bot restart: retried below like a stream that ended.
        }
        if (stopped) return;
        const delay = Math.min(maxDelayMs, baseDelayMs * 2 ** failures);
        failures += 1;
        onStatus("reconnecting");
        retry = setTimeout(() => void connect(), delay);
    };

    onStatus("connecting");
    void connect();
    return () => {
        stopped = true;
        clearTimeout(retry);
        abort.abort();
    };
}
